/**
 * Gift Picker (GWP)
 * ─────────────────────────────────────────────────────────────────────────────
 * Behaviour for the multi-gift banner + carousel rendered by
 * snippets/gift-picker.liquid (only present when 2+ products are configured
 * in the "GWP Products" list). The single-gift path renders plain
 * 'horizontal-product' with no extra markup and needs none of this — it
 * keeps using the theme's existing add-to-cart/quick-buy flow untouched.
 *
 * Three responsibilities:
 *   1. Banner ⇄ picker toggle: "SELECT GIFT" (or the panel's close button)
 *      flips which of the two (already server-rendered) blocks is visible.
 *   2. Carousel chrome: prev/next arrows scroll the track; a scroll listener
 *      keeps the progress fill, "Showing X/Y Gifts" counter and arrow
 *      disabled state in sync with the real scroll position.
 *   3. Swap-on-select: picking a gift removes any OTHER product from the same
 *      configured gift list that's already in the cart first, so the
 *      customer never ends up with two free gifts at once. Intercepted in
 *      the capture phase, which runs before the theme's own <form
 *      is="product-form"> submit handler (attached directly on the form, so
 *      it only ever reaches the target/bubble phase) — see
 *      assets/theme.js's ProductForm class.
 * ─────────────────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  /** @param {HTMLElement} flow @returns {string[]} */
  function getGiftIds(flow) {
    const script = flow.querySelector('script[data-gift-picker-ids]');
    if (!script) return [];
    try {
      const ids = JSON.parse(script.textContent || '[]');
      return Array.isArray(ids) ? ids.map(String) : [];
    } catch (err) {
      return [];
    }
  }

  /**
   * Bin icon on the single-gift card (top-right, see snippets/gift-picker.
   * liquid's data-gift-slide) — dismisses the card AND its tier's
   * <free-shipping-bar> (progress bar + message), client-side only. No cart
   * line exists yet at this point (the card only ever shows before the gift
   * has been added), so there's nothing to remove from the cart and nothing
   * to persist: the next cart:change morph or a page reload re-renders this
   * markup from scratch, bringing both straight back.
   */
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;

    const dismiss = e.target.closest('[data-gift-dismiss]');
    if (!dismiss) return;

    const slide = dismiss.closest('[data-gift-slide]');
    if (!slide) return;

    const tier = slide.getAttribute('data-gwp-tier');
    if (tier) {
      const bar = document.querySelector('free-shipping-bar[data-gwp-tier="' + tier + '"]');
      if (bar) bar.remove();
    }

    slide.remove();
  });

  /* ── Banner ⇄ picker toggle ─────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;

    const toggle = e.target.closest('[data-gift-select-toggle]');
    if (!toggle) return;

    const flow = toggle.closest('[data-gift-flow]');
    if (!flow) return;

    const banner = flow.querySelector('[data-gift-banner]');
    const panel = flow.querySelector('[data-gift-picker-panel]');
    if (!banner || !panel) return;

    const opening = panel.hasAttribute('hidden');
    if (opening) {
      panel.removeAttribute('hidden');
      banner.setAttribute('hidden', '');

      // The track's dimensions read as 0 while the panel was [hidden] (no
      // box is generated), so the initial-load/cart:change sync elsewhere in
      // this file couldn't compute a real state — do it now that it's
      // actually visible and has real layout.
      const track = panel.querySelector('[data-gift-carousel-track]');
      if (track instanceof HTMLElement) syncCarouselState(track);
    } else {
      panel.setAttribute('hidden', '');
      banner.removeAttribute('hidden');
    }
  });

  /* ── Carousel arrows ────────────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;

    const arrow = e.target.closest('[data-gift-carousel-prev], [data-gift-carousel-next]');
    if (!arrow) return;

    const carousel = arrow.closest('[data-gift-carousel]');
    const track = carousel && carousel.querySelector('[data-gift-carousel-track]');
    if (!(track instanceof HTMLElement)) return;

    const firstSlide = track.firstElementChild;
    if (!(firstSlide instanceof HTMLElement)) return;

    const gapValue = window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || '0';
    const step = firstSlide.getBoundingClientRect().width + (parseFloat(gapValue) || 0);
    const direction = arrow.hasAttribute('data-gift-carousel-prev') ? -1 : 1;

    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  });

  /**
   * Drives the "Showing X/Y Gifts" counter, the progress fill, and the
   * prev/next arrow disabled state directly from scroll position (same
   * reasoning as board-multi's gift-variant-picker.js: with ~2 cards visible
   * at once, "current" is derived from scroll position, not a single active
   * slide index).
   *
   * Called both reactively (the 'scroll' listener below) and proactively —
   * on initial script load and after every cart:change, since the drawer's
   * markup can be replaced wholesale by a section morph, giving a fresh
   * track element that's never fired a 'scroll' event yet. Without an
   * up-front call, the arrows just sit in their default (non-disabled) look
   * until the customer happens to scroll once themselves.
   * @param {HTMLElement} track
   */
  function syncCarouselState(track) {
    const carousel = track.closest('[data-gift-carousel]');
    const progressRow = carousel && carousel.parentElement.querySelector('[data-gift-total]');
    if (!(progressRow instanceof HTMLElement)) return;

    const total = parseInt(progressRow.dataset.giftTotal || '', 10);
    const firstSlide = track.firstElementChild;
    if (!total || !(firstSlide instanceof HTMLElement)) return;

    const gapValue = window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || '0';
    const step = firstSlide.getBoundingClientRect().width + (parseFloat(gapValue) || 0);
    if (!step) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 1;
    const atEnd = maxScroll <= 0 || track.scrollLeft >= maxScroll - 1;

    let current;
    if (atEnd) {
      current = total;
    } else {
      const rightEdge = track.scrollLeft + track.clientWidth;
      current = Math.max(1, Math.min(total, Math.round(rightEdge / step)));
    }

    const currentEl = progressRow.querySelector('[data-gift-counter-current]');
    if (currentEl) currentEl.textContent = String(current);

    const fillEl = progressRow.querySelector('[data-gift-progress-fill]');
    if (fillEl instanceof HTMLElement) {
      // Matches the "Showing X/Y Gifts" counter above (current/total) rather
      // than raw scroll position — those two disagreed at rest: 2 cards are
      // already visible before any scrolling happens at all, so a
      // scroll-based fill sat at 0% while the counter already said "2/3".
      const progress = current / total;
      fillEl.style.width = (progress * 100).toFixed(2) + '%';
    }

    if (carousel instanceof HTMLElement) {
      const prevBtn = carousel.querySelector('[data-gift-carousel-prev]');
      const nextBtn = carousel.querySelector('[data-gift-carousel-next]');
      if (prevBtn instanceof HTMLButtonElement) prevBtn.disabled = atStart;
      if (nextBtn instanceof HTMLButtonElement) nextBtn.disabled = atEnd;
    }
  }

  function syncAllCarousels() {
    document.querySelectorAll('[data-gift-carousel-track]').forEach(syncCarouselState);
  }

  document.addEventListener(
    'scroll',
    function (e) {
      const track = e.target;
      if (!(track instanceof HTMLElement) || !track.matches('[data-gift-carousel-track]')) return;
      syncCarouselState(track);
    },
    true
  );

  // Initial state: nothing has scrolled yet, but the arrows/progress still
  // need to reflect "showing card 1" rather than their unstyled defaults.
  syncAllCarousels();

  // The drawer's markup can be replaced wholesale by a cart:change morph —
  // recompute for whatever track exists afterward rather than assuming the
  // previous one (and its scroll position) survived.
  document.documentElement.addEventListener('cart:change', syncAllCarousels);

  /**
   * Mono doesn't expose section ids via a data attribute — every other cart
   * mutation (ProductForm, LineItemQuantity in theme.js) instead dispatches
   * `cart:prepare-bundled-sections` and lets each interested component (e.g.
   * <cart-drawer>'s own _onPrepareBundledSections) push its own section id
   * into the array. Skipping this handshake would leave cart.sections empty,
   * and <cart-drawer>'s _onCartChanged indexes into it unconditionally
   * (`cart.sections[extractSectionId(this)]`) — so an empty sections map
   * throws instead of just no-op'ing.
   * @returns {string[]}
   */
  function getCartSectionIds() {
    /** @type {string[]} */
    const sectionIds = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionIds }
      })
    );
    return sectionIds;
  }

  /**
   * @param {object} cart plain /cart.js payload
   * @param {object} [sections] section-id → html map from an add/change response.
   *   <cart-drawer>'s _onCartChanged indexes into cart.sections unconditionally,
   *   so this always sets it (even to {}) rather than leaving it undefined.
   */
  function dispatchGiftCartChange(cart, sections) {
    cart.sections = sections || {};
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart: cart, sourceId: 'gift-picker' }
      })
    );
  }

  function isCartPage() {
    const settings = window.themeVariables && window.themeVariables.settings;
    return !!settings && (settings.cartType === 'page' || settings.pageType === 'cart');
  }

  /**
   * Removes any cart line whose product is in `giftIds` AND is actually a
   * gift line (properties._is_gwp) — matching on product id alone would
   * also catch a customer's separate, full-price purchase of that same
   * product (e.g. it's a real item they bought AND happens to be one of the
   * configured gift options), silently deleting a real purchase instead of
   * a stale/duplicate gift pick. Requests section html on the LAST removal
   * only (no point re-rendering after every single change.js call), so the
   * caller can reuse it instead of another round trip.
   * @param {Array<{key: string, product_id: (string|number), properties?: Record<string, string>}>} items
   * @param {string[]} giftIds
   * @returns {Promise<object|null>} the last change.js response, or null if nothing matched
   */
  async function removeGifts(items, giftIds) {
    const toRemove = items.filter(function (item) {
      return (
        giftIds.indexOf(String(item.product_id)) !== -1 &&
        item.properties &&
        item.properties['_is_gwp'] === 'true'
      );
    });
    if (!toRemove.length) return null;

    const sectionIds = getCartSectionIds();
    let lastResult = null;
    for (const item of toRemove) {
      const payload = { id: item.key, quantity: 0 };
      const isLast = item === toRemove[toRemove.length - 1];
      if (isLast && sectionIds.length) {
        payload.sections = sectionIds.join(',');
        payload.sections_url = window.location.pathname;
      }
      const res = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      lastResult = await res.json();
    }
    return lastResult;
  }

  /**
   * Full ownership of a gift card's <form is="product-form"> submit inside
   * the carousel: removes any OTHER product from this same gift list that's
   * already in the cart, THEN adds the newly chosen one — a plain add here
   * would otherwise leave two free gifts in the cart at once.
   *
   * On the cart page, product-form normally does a full-page redirect to
   * /cart instead of an AJAX add (see assets/theme.js's ProductForm) — this
   * mirrors that once the swap's removal has gone through, instead of always
   * AJAX-adding regardless of page context.
   * @param {HTMLFormElement} form
   */
  async function handleGiftFormSubmit(form) {
    const flow = form.closest('[data-gift-flow]');
    const giftIds = flow ? getGiftIds(flow) : [];

    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn instanceof HTMLButtonElement) {
      if (submitBtn.disabled) return;
      submitBtn.setAttribute('aria-busy', 'true');
    }

    try {
      if (giftIds.length) {
        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        await removeGifts(cart.items, giftIds);
      }

      if (isCartPage()) {
        form.submit();
        return;
      }

      const sectionIds = getCartSectionIds();
      const formData = new FormData(form);
      if (sectionIds.length) {
        formData.set('sections', sectionIds.join(','));
        formData.set('sections_url', window.location.pathname);
      }

      const addRes = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const addData = await addRes.json();

      if (!addRes.ok) {
        form.dispatchEvent(
          new CustomEvent('cart:error', { bubbles: true, detail: { error: addData.description || addData.message } })
        );
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      dispatchGiftCartChange(cartContent, addData.sections);
    } catch (err) {
      console.error('[gift-picker] swap/add error:', err);
    } finally {
      if (submitBtn instanceof HTMLButtonElement) submitBtn.removeAttribute('aria-busy');
    }
  }

  /**
   * Primary interception point: the submit BUTTON's click, not the form's
   * 'submit' event. If the browser considers the form invalid when the
   * button is clicked, it silently blocks the 'submit' event from ever
   * firing at all (native constraint validation runs first) and shows a
   * validation tooltip instead — one that renders invisible/clipped here
   * since the field lives inside .gift-picker-carousel-track's
   * `overflow-x: auto`, a well-known interaction between validation bubbles
   * and scrollable ancestors. That combination looks exactly like "nothing
   * happens": no console error, no network request, no visible reaction.
   * Intercepting the click itself sidesteps native validation entirely
   * rather than depending on an event it can prevent from ever occurring.
   */
  document.addEventListener(
    'click',
    function (e) {
      if (!(e.target instanceof Element)) return;

      const submitBtn = e.target.closest('[type="submit"]');
      if (!submitBtn || !submitBtn.closest('[data-gift-carousel]')) return;

      const form = submitBtn.form;
      if (!(form instanceof HTMLFormElement)) return;

      e.preventDefault();
      e.stopPropagation();

      handleGiftFormSubmit(form);
    },
    true
  );

  /**
   * Backstop for non-click submissions (e.g. pressing Enter while focused
   * in the size select) — those fire 'submit' directly without a button
   * 'click' ever occurring, so the handler above wouldn't see them.
   */
  document.addEventListener(
    'submit',
    function (e) {
      if (!(e.target instanceof HTMLFormElement)) return;
      if (!e.target.closest('[data-gift-carousel]')) return;

      e.preventDefault();
      e.stopPropagation();

      handleGiftFormSubmit(e.target);
    },
    true
  );

  /**
   * Backstop: whenever the cart changes (including via the quick-buy-drawer
   * path for gift products with multiple option types, which the submit
   * intercept above doesn't cover), keep at most one line per configured
   * gift list — the most recently added one wins, any earlier ones for the
   * same list are removed.
   * @param {CustomEvent} e
   */
  document.documentElement.addEventListener('cart:change', function (e) {
    if (e.detail && e.detail.sourceId === 'gift-picker') return;

    const cart = e.detail && e.detail.cart;
    if (!cart || !Array.isArray(cart.items)) return;

    document.querySelectorAll('[data-gift-flow]').forEach(function (flow) {
      const giftIds = getGiftIds(flow);
      if (!giftIds.length) return;

      // _is_gwp, not just product id — a customer's separate full-price
      // purchase of the same product must never count as a duplicate pick.
      const matches = cart.items.filter(function (item) {
        return (
          giftIds.indexOf(String(item.product_id)) !== -1 &&
          item.properties &&
          item.properties['_is_gwp'] === 'true'
        );
      });
      if (matches.length < 2) return;

      // Keep the most recently added line, remove the earlier one(s).
      const toRemove = matches.slice(0, -1).map(function (item) {
        return item.key;
      });

      (async function () {
        const result = await removeGifts(
          cart.items.filter(function (item) {
            return toRemove.indexOf(item.key) !== -1;
          }),
          giftIds
        );
        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        dispatchGiftCartChange(cartContent, result && result.sections);
      })();
    });
  });

  /**
   * Every configured GWP tier (product ids + threshold + customer-only flag,
   * in cart.js's cents format) — rendered unconditionally by
   * gift-picker.liquid regardless of whether that tier's card/picker is
   * currently showing (it isn't, once a gift's already been added — see
   * any_gift_selected there), since this needs to keep working precisely in
   * that state. Each tier is independent: a second tier can have a different
   * customer_only value than the first, so this must never be collapsed into
   * a single global flag (see window.gwp.enable_customer in theme.js, which
   * is exactly that mistake — it only reflects tier 1 and, if it removed
   * lines itself, would wrongly sweep up a tier-2 gift a logged-out guest is
   * legitimately entitled to).
   * @returns {Array<{ids: string[], threshold: number, customerOnly: boolean}>}
   */
  function getGwpTiers() {
    const tiers = [];
    document.querySelectorAll('script[data-gwp-gift-ids]').forEach(function (script) {
      let ids;
      try {
        ids = JSON.parse(script.textContent || '[]');
      } catch (err) {
        return;
      }
      if (!Array.isArray(ids) || !ids.length) return;

      const threshold = parseFloat(script.getAttribute('data-gwp-threshold') || '');
      if (!threshold) return;

      const customerOnly = script.getAttribute('data-gwp-customer-only') === 'true';

      tiers.push({ ids: ids.map(String), threshold: threshold, customerOnly: customerOnly });
    });
    return tiers;
  }

  /**
   * Same qualifying-subtotal formula as snippets/gwp.liquid: shipping-
   * required line prices, minus cart-level discounts, minus the gift's own
   * line price (so the free gift itself never counts toward re-qualifying
   * for itself).
   * @param {object} cart
   * @param {string[]} giftIds
   * @returns {number}
   */
  function computeQualifyingSubtotal(cart, giftIds) {
    let total = 0;
    let gwpPrice = 0;
    cart.items.forEach(function (item) {
      if (item.requires_shipping) total += item.final_line_price;
      const isConfiguredGift = giftIds.indexOf(String(item.product_id)) !== -1;
      if (isConfiguredGift && item.properties && item.properties['_is_gwp'] === 'true') {
        gwpPrice += item.final_line_price;
      }
    });
    const discount = (cart.cart_level_discount_applications || []).reduce(function (sum, discountApplication) {
      return sum + discountApplication.total_allocated_amount;
    }, 0);
    return total - discount - gwpPrice;
  }

  /**
   * Removes the lines matching whichever tiers `shouldRemove` flags, given a
   * fresh cart payload. Shared by the threshold check and the customer-only
   * check below — each calls this with its own tier predicate so a
   * tier-1-only condition can never remove a tier-2 line, or vice versa.
   * @param {object} cart plain /cart.js payload
   * @param {(tier: {ids: string[], threshold: number, customerOnly: boolean}) => boolean} shouldRemove
   */
  function removeGiftsForTiers(cart, shouldRemove) {
    if (!cart || !Array.isArray(cart.items)) return;

    getGwpTiers().forEach(function (tier) {
      if (!shouldRemove(tier)) return;

      const toRemove = cart.items.filter(function (item) {
        return (
          tier.ids.indexOf(String(item.product_id)) !== -1 &&
          item.properties &&
          item.properties['_is_gwp'] === 'true'
        );
      });
      if (!toRemove.length) return;

      (async function () {
        const result = await removeGifts(toRemove, tier.ids);
        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        dispatchGiftCartChange(cartContent, result && result.sections);
      })();
    });
  }

  /**
   * Auto-removes an already-added gift once it no longer qualifies for its
   * OWN tier — either the cart total dropped back below that tier's
   * threshold, or that tier is customer-only and the customer is now logged
   * out — the eligibility-gated Liquid rendering hides the card/picker in
   * either case, but the cart LINE ITEM itself has no such gate and would
   * otherwise stay in the cart forever once added.
   * @param {object} cart plain /cart.js payload
   */
  function checkGwpTiers(cart) {
    if (!cart) return;
    const loggedIn = !!(window.customer && window.customer.id);
    removeGiftsForTiers(cart, function (tier) {
      const belowThreshold = computeQualifyingSubtotal(cart, tier.ids) < tier.threshold;
      const customerLoggedOut = tier.customerOnly && !loggedIn;
      return belowThreshold || customerLoggedOut;
    });
  }

  // Deliberately does NOT skip sourceId === 'gift-picker' events (unlike the
  // dedupe-backstop listener above) — removing one tier's now-unqualified
  // gift dispatches exactly that kind of event, and if THAT event were
  // ignored here, a second tier that becomes unqualified only as a side
  // effect of the first tier's removal (e.g. tier 1 drops below its own
  // threshold once tier 2's un-discounted, still full-price gift line is
  // gone) would never get re-checked at all. Safe to let this cascade: each
  // pass either removes a line (dispatching another cart:change that gets
  // checked again) or finds nothing left to remove and stops, so it's
  // bounded by the number of configured tiers, never infinite.
  document.documentElement.addEventListener('cart:change', function (e) {
    checkGwpTiers(e.detail && e.detail.cart);
  });

  // Also check once on load, straight from a fresh /cart.js fetch — but only
  // on the actual cart PAGE, where assets/theme.js's LineItemQuantity does a
  // full page reload on quantity change instead of dispatching cart:change
  // at all, so a stale gift dropped below threshold that way would never get
  // caught by the listener above; a fresh page load needs to check for
  // itself there. Deliberately NOT done unconditionally on every page load:
  // in the cart DRAWER, quantity changes already dispatch cart:change
  // correctly (no reload), so this extra check only adds a chance of an
  // unprompted background removal + full drawer morph firing the moment the
  // page loads — which can land right as the customer clicks something else
  // in the drawer, on an element that's mid-replacement.
  if (isCartPage()) {
    fetch(`${Shopify.routes.root}cart.js`)
      .then(function (res) {
        return res.json();
      })
      .then(checkGwpTiers)
      .catch(function (err) {
        console.error('[gift-picker] initial threshold check failed:', err);
      });
  }

  // Customer-only removal, separately, DOES run unconditionally on every
  // page load (not just /cart) — this is what theme.js's FreeShippingBar
  // #_onCheckGWP used to do on its own 3s-delayed connectedCallback timer,
  // before that logic moved here to become tier-aware. Safe to run
  // unconditionally, unlike the threshold check above: "logged out" can't
  // change without a full page navigation (which re-runs this from
  // scratch), so there's no equivalent risk of an unprompted mid-session
  // background morph landing on an interaction in progress.
  fetch(`${Shopify.routes.root}cart.js`)
    .then(function (res) {
      return res.json();
    })
    .then(function (cart) {
      const loggedIn = !!(window.customer && window.customer.id);
      if (loggedIn) return;
      removeGiftsForTiers(cart, function (tier) {
        return tier.customerOnly;
      });
    })
    .catch(function (err) {
      console.error('[gift-picker] initial customer-only check failed:', err);
    });
})();
